import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import Header from '../../components/ui/UnifiedHeader';
import SharedFooter from '../../components/SharedFooter';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import StickySubNav from '../../components/ui/StickySubNav';
import { useAnchorScroll } from '../../hooks/useAnchorScroll';
import { MARKET_OVERVIEW, CITIES, DATA_AS_OF } from '../../constants/marketData';

const GCCExpansionServices = () => {
  useAnchorScroll();

  const sections = [
    { id: 'expansion-playbooks', name: 'Expansion Playbooks', icon: 'Map' },
    { id: 'government-partnerships', name: 'Government Partnerships', icon: 'Building2' },
    { id: 'market-intelligence', name: 'Market Intelligence', icon: 'TrendingUp' }
  ];

  const expansionPlaybooks = [
    {
      title: 'Multi-City Roadmap',
      description: 'Strategic framework for expanding from single to multiple GCC locations across India',
      icon: 'Map',
      features: [
        'City selection criteria and scoring',
        'Phased rollout timeline (6-18 months)',
        'Resource allocation frameworks',
        'Risk mitigation strategies'
      ]
    },
    {
      title: 'Governance Framework',
      description: 'Operating models and decision-making structures for scaled GCC operations',
      icon: 'Shield',
      features: [
        'Centralized vs federated models',
        'Reporting hierarchies',
        'Performance metrics and KPIs',
        'Compliance and audit frameworks'
      ]
    },
    {
      title: 'Operating Playbooks',
      description: 'Day-to-day operational guidelines for running multiple GCC sites',
      icon: 'BookOpen',
      features: [
        'Standard operating procedures',
        'Cross-site collaboration tools',
        'Knowledge management systems',
        'Continuous improvement processes'
      ]
    }
  ];

  const governmentPartnerships = [
    {
      title: 'State Incentive Programs',
      description: 'Access to government incentives across Indian states',
      icon: 'Building2',
      states: ['Karnataka', 'Telangana', 'Tamil Nadu', 'Maharashtra', 'Gujarat'],
      benefits: [
        'Capital subsidies up to 25%',
        'Land at concessional rates',
        'Power tariff rebates',
        'Stamp duty exemptions'
      ]
    },
    {
      title: 'EODB (Ease of Doing Business)',
      description: 'Streamlined regulatory approvals and single-window clearances',
      icon: 'FileCheck',
      features: [
        'Single-window clearance facilitation',
        'Regulatory compliance support',
        'License and permit assistance',
        'Labor law compliance guidance'
      ]
    },
    {
      title: 'Special Economic Zones',
      description: 'SEZ benefits and IT/ITES park advantages',
      icon: 'Landmark',
      benefits: [
        'Tax holidays and exemptions',
        'Duty-free imports',
        'Simplified customs procedures',
        'Infrastructure advantages'
      ]
    }
  ];

  const marketInsights = [
    {
      metric: 'Active India GCCs',
      value: MARKET_OVERVIEW.totalGccs.value,
      trend: MARKET_OVERVIEW.yoyHeadcountGrowth.value + ' YoY',
      icon: 'Building2',
      source: MARKET_OVERVIEW.totalGccs.source
    },
    {
      metric: 'GCC Professionals',
      value: MARKET_OVERVIEW.totalEmployment.value,
      trend: MARKET_OVERVIEW.totalEmployment.label,
      icon: 'Users',
      source: MARKET_OVERVIEW.totalEmployment.source
    },
    {
      metric: 'Cost Savings vs US',
      value: MARKET_OVERVIEW.costSavingsVsUS.value,
      trend: 'Typical range',
      icon: 'TrendingDown',
      source: MARKET_OVERVIEW.costSavingsVsUS.source
    },
    {
      metric: 'Industry Revenue (FY24)',
      value: MARKET_OVERVIEW.marketSize.value,
      trend: MARKET_OVERVIEW.yoyRevenueGrowth.value + ' YoY',
      icon: 'DollarSign',
      source: MARKET_OVERVIEW.marketSize.source
    }
  ];

  // Top GCC cities derived from MARKET_DATA — show 6 tier-1 hubs in the expansion table
  const topCities = CITIES.filter((c) => c.tier === 1).map((c) => ({
    name: c.name,
    talent: c.talentPool,
    specialization: c.primaryVerticals.slice(0, 2).join(' / '),
    source: c.talentPoolNote
  }));

  return (
    <>
      <Helmet>
        <title>GCC Expansion Services - Multi-City Growth & Government Partnerships | Bootminds</title>
        <meta 
          name="description" 
          content="Expand your GCC footprint across India with our expansion playbooks, government partnership facilitation, and real-time market intelligence." 
        />
        <link rel="canonical" href="/services/expansion" />
      </Helmet>

      <div className="min-h-screen bg-background">
        <Header />
        
        <main className="pt-16 lg:pt-20">
          {/* Hero Section */}
          <section className="py-20 bg-gradient-to-br from-primary/5 via-background to-accent/5 relative overflow-hidden">
            <div className="container-executive">
              <div className="text-center max-w-4xl mx-auto">
                <div className="inline-flex items-center space-x-2 bg-accent/10 text-accent px-4 py-2 rounded-full text-sm font-body-medium mb-6">
                  <Icon name="Globe2" size={16} />
                  <span>Scale Your India Operations</span>
                </div>
                
                <h1 className="font-headline font-headline-bold text-4xl lg:text-5xl text-text-primary mb-6">
                  GCC Expansion Services
                  <span className="block text-accent">Multi-City Growth & Market Intelligence</span>
                </h1>
                
                <p className="text-xl text-text-secondary max-w-3xl mx-auto mb-8">
                  Accelerate your GCC expansion with proven playbooks, government partnership facilitation, 
                  and real-time market intelligence across India's top technology hubs.
                </p>

                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button 
                    variant="default"
                    size="lg"
                    className="bg-primary text-primary-foreground hover:bg-primary/90"
                    onClick={() => document.getElementById('expansion-playbooks')?.scrollIntoView({ behavior: 'smooth' })}
                  >
                    <Icon name="Map" size={18} className="mr-2" />
                    Explore Playbooks
                  </Button>
                  <Button 
                    variant="outline"
                    size="lg"
                    className="border-primary/50 text-text-primary hover:bg-primary/10"
                    onClick={() => document.getElementById('market-intelligence')?.scrollIntoView({ behavior: 'smooth' })}
                  >
                    <Icon name="TrendingUp" size={18} className="mr-2" />
                    View Market Data
                  </Button>
                </div>
              </div>
            </div>
          </section>

          {/* Navigation Tabs */}
          <StickySubNav sections={sections} topOffset="top-16 lg:top-20" />

          {/* Expansion Playbooks Section */}
          <section id="expansion-playbooks" className="py-20 bg-background">
            <div className="container-executive">
              {/* Main Content Grid */}
              <div className="grid gap-10 lg:grid-cols-2 items-start mb-16">
                <div>
                  <div className="inline-flex items-center space-x-2 rounded-full bg-primary/10 px-4 py-2 text-sm font-medium text-text-primary mb-4">
                    <Icon name="Map" size={16} />
                    <span>Multi-City Scale Blueprint</span>
                  </div>
                  <h2 className="text-3xl font-headline font-bold text-text-primary mb-4">
                    Expansion Playbooks for Tier-1, Tier-2 & Global Hubs
                  </h2>
                  <p className="text-lg text-text-secondary mb-6">
                    We orchestrate location strategy, talent planning, and governance so every new site extends your global operating fabric—not just your seat count. Each playbook includes readiness scoring, financial models, and execution sprints.
                  </p>
                  <ul className="space-y-3">
                    {[
                      'Location heatmaps covering India Tier-1, Tier-2, and global hubs like Singapore/Dubai',
                      'Operating model design covering command center, spoke hubs, and nearshore pods',
                      'Regulatory and compliance pathway with risk mitigations pre-approved',
                      'Executive cockpit for expansion tracking with milestone-linked SLAs'
                    ].map((item, index) => (
                      <li key={index} className="flex items-start space-x-3 text-text-secondary">
                        <Icon name="CheckCircle2" size={18} className="text-success mt-0.5" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="rounded-3xl border border-border bg-card/80 p-8 shadow-lg">
                  <h3 className="text-xl font-semibold text-text-primary mb-4">Expansion Roadmap Snapshot</h3>
                  <div className="space-y-5">
                    {[
                      {
                        label: 'Discovery Sprint',
                        description: '4-week assessment covering demand signals, attrition risk, and competitor positioning.'
                      },
                      {
                        label: 'Design & Incentives',
                        description: 'State and SEZ comparisons, ecosystem mapping, and incentive negotiation playbook.'
                      },
                      {
                        label: 'Activation & Governance',
                        description: 'Change management, site launch sequencing, and cross-location governance council.'
                      }
                    ].map((step) => (
                      <div key={step.label} className="rounded-2xl border border-primary/20 bg-primary/5 p-4">
                        <p className="text-sm uppercase tracking-wide text-text-primary">{step.label}</p>
                        <p className="text-sm text-text-secondary mt-1">{step.description}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Playbook Cards */}
              <div className="grid md:grid-cols-3 gap-8">
                {expansionPlaybooks.map((playbook, index) => (
                  <motion.div
                    key={playbook.title}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="bg-card rounded-xl p-6 border border-border hover:border-primary/30 transition-colors"
                  >
                    <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                      <Icon name={playbook.icon} size={24} className="text-primary" />
                    </div>
                    <h3 className="font-headline font-headline-weight text-xl text-text-primary mb-2">
                      {playbook.title}
                    </h3>
                    <p className="text-text-secondary text-sm mb-4">
                      {playbook.description}
                    </p>
                    <ul className="space-y-2">
                      {playbook.features.map((feature, i) => (
                        <li key={i} className="flex items-start space-x-2 text-sm text-text-secondary">
                          <Icon name="Check" size={16} className="text-accent mt-0.5 flex-shrink-0" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>

          {/* Government Partnerships Section */}
          <section id="government-partnerships" className="py-20 bg-muted/30">
            <div className="container-executive">
              {/* Main Content Grid */}
              <div className="grid gap-10 lg:grid-cols-2 items-center mb-16">
                <div className="order-last lg:order-first">
                  <div className="rounded-3xl border border-success/20 bg-success/10 p-8 shadow-lg">
                    <h3 className="text-xl font-semibold text-success mb-4">Government Incentive Toolkit</h3>
                    <ul className="space-y-3 text-success/90">
                      <li className="flex items-start space-x-3">
                        <Icon name="Handshake" size={18} className="mt-0.5" />
                        <span>State-level incentive negotiation with pre-vetted partners in India.</span>
                      </li>
                      <li className="flex items-start space-x-3">
                        <Icon name="Globe2" size={18} className="mt-0.5" />
                        <span>Singapore EDB introductions focused on data engineering & AI talent clusters.</span>
                      </li>
                      <li className="flex items-start space-x-3">
                        <Icon name="Building" size={18} className="mt-0.5" />
                        <span>Dubai pathways for niche leadership pods with clarity on talent depth & cost.</span>
                      </li>
                      <li className="flex items-start space-x-3">
                        <Icon name="FileText" size={18} className="mt-0.5" />
                        <span>Government-ready dossiers: job creation analytics, infrastructure commitments, and ESG narratives.</span>
                      </li>
                    </ul>
                  </div>
                </div>
                <div>
                  <div className="inline-flex items-center space-x-2 rounded-full bg-success/10 px-4 py-2 text-sm font-medium text-success mb-4">
                    <Icon name="Building2" size={16} />
                    <span>Government Partnerships</span>
                  </div>
                  <h2 className="text-3xl font-headline font-bold text-text-primary mb-4">
                    Incentive Negotiations & Government Access Without the Red Tape
                  </h2>
                  <p className="text-lg text-text-secondary mb-6">
                    From India's state governments to Singapore's digital economy leaders, we connect you with decision-makers who unlock infrastructure, incentive, and fast-track approvals. Every outreach is backed by market data, talent forecasts, and economic impact assessments.
                  </p>
                  <div className="grid gap-4 sm:grid-cols-2">
                    {[
                      {
                        label: 'India',
                        detail: '15+ state relationships, GCC incentive benchmarking, talent pool analytics.'
                      },
                      {
                        label: 'Singapore',
                        detail: 'FinTech & AI talent corridors with government co-funding support.'
                      },
                      {
                        label: 'Dubai',
                        detail: 'Leadership pods and hybrid GCC structures with clear ROI signals.'
                      },
                      {
                        label: 'Compliance',
                        detail: 'Entity, labour law, and data residency policy guides for each jurisdiction.'
                      }
                    ].map((item) => (
                      <div key={item.label} className="rounded-xl border border-border p-4 bg-card/80">
                        <p className="text-sm font-semibold text-text-primary">{item.label}</p>
                        <p className="text-sm text-text-secondary mt-1">{item.detail}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Partnership Cards */}
              <div className="grid md:grid-cols-3 gap-8">
                {governmentPartnerships.map((partnership, index) => (
                  <motion.div
                    key={partnership.title}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="bg-card rounded-xl p-6 border border-border"
                  >
                    <div className="w-12 h-12 bg-accent/10 rounded-lg flex items-center justify-center mb-4">
                      <Icon name={partnership.icon} size={24} className="text-accent" />
                    </div>
                    <h3 className="font-headline font-headline-weight text-xl text-text-primary mb-2">
                      {partnership.title}
                    </h3>
                    <p className="text-text-secondary text-sm mb-4">
                      {partnership.description}
                    </p>
                    {partnership.states && (
                      <div className="mb-4">
                        <p className="text-xs text-text-secondary mb-2">Active in:</p>
                        <div className="flex flex-wrap gap-1">
                          {partnership.states.map((state) => (
                            <span key={state} className="text-xs bg-primary/10 text-primary px-2 py-1 rounded">
                              {state}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    <ul className="space-y-2">
                      {(partnership.benefits || partnership.features).map((item, i) => (
                        <li key={i} className="flex items-start space-x-2 text-sm text-text-secondary">
                          <Icon name="Check" size={16} className="text-accent mt-0.5 flex-shrink-0" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>

          {/* Market Intelligence Section */}
          <section id="market-intelligence" className="py-20 bg-background">
            <div className="container-executive">
              <div className="text-center mb-12">
                <h2 className="font-headline font-headline-bold text-3xl text-text-primary mb-4">
                  Market Intelligence
                </h2>
                <p className="text-text-secondary max-w-2xl mx-auto">
                  Public-data benchmarks for India GCC expansion decisions. Data as of {DATA_AS_OF}.
                </p>
              </div>

              {/* Key Metrics */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                {marketInsights.map((insight) => (
                  <div key={insight.metric} className="bg-card rounded-xl p-6 border border-border text-center">
                    <Icon name={insight.icon} size={24} className="text-primary mx-auto mb-3" />
                    <p className="text-2xl font-headline font-headline-bold text-text-primary">{insight.value}</p>
                    <p className="text-sm text-text-secondary">{insight.metric}</p>
                    <span className="text-xs text-text-secondary">
                      {insight.trend}
                    </span>
                    <p className="text-xs text-text-secondary/70 mt-2">{insight.source}</p>
                  </div>
                ))}
              </div>
              <p className="text-xs text-center text-text-secondary mb-12">
                <Link to="/market-intelligence" className="text-primary hover:underline">See full sources &amp; methodology →</Link>
              </p>

              {/* Top Cities */}
              <div className="bg-card rounded-xl border border-border overflow-hidden">
                <div className="p-6 border-b border-border">
                  <h3 className="font-headline font-headline-weight text-xl text-text-primary">
                    Top Expansion Destinations
                  </h3>
                </div>
                <div className="divide-y divide-border">
                  {topCities.map((city, index) => (
                    <div key={city.name} className="p-4 flex items-center justify-between hover:bg-muted/50 transition-colors">
                      <div className="flex items-center space-x-4">
                        <span className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center text-primary font-body-medium">
                          {index + 1}
                        </span>
                        <div>
                          <p className="font-body-medium text-text-primary">{city.name}</p>
                          <p className="text-sm text-text-secondary">{city.specialization}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-body-medium text-primary">{city.talent}</p>
                        <p className="text-xs text-text-secondary">Available Talent</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* CTA to full Market Intelligence */}
              <div className="mt-8 text-center">
                <Link to="/market-intelligence">
                  <Button variant="outline">
                    <Icon name="ExternalLink" size={18} className="mr-2" />
                    Explore Full Market Intelligence Hub
                  </Button>
                </Link>
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section className="py-20 bg-gradient-to-br from-primary/10 to-accent/10">
            <div className="container-executive">
              <div className="text-center max-w-3xl mx-auto">
                <h2 className="font-headline font-headline-bold text-3xl text-text-primary mb-4">
                  Ready to Expand Your GCC?
                </h2>
                <p className="text-text-secondary mb-8">
                  Let our expansion specialists help you plan your multi-city growth strategy with 
                  government partnerships and market intelligence.
                </p>
                <Link to="/contact">
                  <Button variant="primary" size="lg">
                    <Icon name="Calendar" size={18} className="mr-2" />
                    Schedule Expansion Consultation
                  </Button>
                </Link>
              </div>
            </div>
          </section>
        </main>

        <SharedFooter />
      </div>
    </>
  );
};

export default GCCExpansionServices;
